import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faHome, faCalendarAlt, faClock, faMapMarkerAlt, faDollarSign, faMoneyBillWave, faHandHoldingUsd } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-toastify';
import './ToursPanel.css';

interface ToursPanelProps {
  isOpen: boolean;
  onClose: () => void;
  tourProperties: any[];
  onRemoveFromTour: (mlsNumber: string) => void;
  onScheduleTour: () => void;
}

const ToursPanel: React.FC<ToursPanelProps> = ({ 
  isOpen, 
  onClose, 
  tourProperties, 
  onRemoveFromTour,
  onScheduleTour 
}) => {
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [formData, setFormData] = useState({
    preferredDate: '',
    preferredTimeSlot: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    financingType: '',
    additionalDates: '',
    notes: ''
  });
  const [showPreQualQuestion, setShowPreQualQuestion] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  // Format phone number as user types
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (555) 123-4567
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length <= 10) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  // Check if user is logged in and fetch profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem('customerPortalToken') || localStorage.getItem('auth_token');
      
      if (token) {
        try {
          setIsLoadingProfile(true);
          const response = await axiosInstance.get('/user/profile');
          
          if (response.data.success && response.data.user) {
            const user = response.data.user;
            
            // Pre-fill form with user data
            setFormData(prevData => ({
              ...prevData,
              customerName: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '',
              customerEmail: user.email || '',
              customerPhone: user.phone || user.phoneNumber || ''
            }));
          }
        } catch (error) {
          console.error('Failed to fetch user profile:', error);
          // Silently fail - form will remain empty
        } finally {
          setIsLoadingProfile(false);
        }
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    // Auto-show schedule form if there are properties
    if (tourProperties.length > 0 && !showScheduleForm) {
      setShowScheduleForm(true);
    }
  }, [tourProperties.length]);

  const handleScheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.preferredDate || !formData.preferredTimeSlot || !formData.customerName || !formData.customerEmail || !formData.customerPhone || !formData.financingType) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await axiosInstance.post('/property-tours/create', {
        properties: tourProperties.map(p => ({
          mlsNumber: p.mlsNumber,
          address: typeof p.address === 'string' 
            ? p.address 
            : `${p.address.street}, ${p.address.city}, ${p.address.state}`,
          price: p.price,
          bedrooms: p.bedrooms,
          bathrooms: p.bathrooms,
          sqft: p.sqft,
          images: p.images || []
        })),
        ...formData
      });

      if (response.data.success) {
        // Show success message with SMS notification
        toast.success(
          <div>
            <strong>✅ Tour Scheduled!</strong><br/>
            <span style={{ fontSize: '14px' }}>Check your phone - we just texted you confirmation!</span>
          </div>,
          {
            autoClose: 5000,
            style: { 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white'
            }
          }
        );
        
        onScheduleTour();
        onClose();
        
        // Clear form
        setFormData({
          preferredDate: '',
          preferredTimeSlot: '',
          customerName: '',
          customerEmail: '',
          customerPhone: '',
          financingType: '',
          additionalDates: '',
          notes: ''
        });
      }
    } catch (error) {
      console.error('Failed to submit tour request:', error);
      toast.error('Failed to submit tour request. Please try again.');
    }
  };

  const timeSlots = [
    'Morning (9AM - 12PM)',
    'Afternoon (12PM - 3PM)',
    'Late Afternoon (3PM - 6PM)',
    'Evening (6PM - 8PM)',
    'Flexible'
  ];

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className={`tours-panel ${isOpen ? 'open' : ''}`}>
      <div className="tours-panel-header">
        <h3>
          <FontAwesomeIcon icon={faHome} className="me-2" />
          Your Tour List
        </h3>
        <Button 
          variant="link" 
          onClick={onClose}
          className="close-btn"
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </div>

      <div className="tours-panel-content">
        {tourProperties.length === 0 ? (
          <div className="empty-state">
            <p className="text-muted">No properties selected for tour yet.</p>
            <p className="text-muted small">Click "Add to Tour" on properties you'd like to visit.</p>
          </div>
        ) : (
          <>
            {/* Properties List */}
            <div className="tour-properties-section">
              <h5 className="section-title">Properties to Tour ({tourProperties.length})</h5>
              <div className="tour-properties-list">
                {tourProperties.map((property) => (
                  <Card key={property.mlsNumber} className="tour-property-card">
                    <Card.Body>
                      <div className="property-info">
                        <img 
                          src={property.images?.[0] || '/placeholder-home.jpg'} 
                          alt={typeof property.address === 'string' ? property.address : property.address.street}
                          className="property-thumbnail"
                        />
                        <div className="property-details">
                          <h6>{typeof property.address === 'string' 
                            ? property.address.split(',')[0] 
                            : property.address.street}</h6>
                          <p className="text-muted mb-0">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                            {typeof property.address === 'string' 
                              ? property.address.split(',').slice(1).join(',').trim() 
                              : `${property.address.city}, ${property.address.state}`}
                          </p>
                          <p className="property-price mb-0">${property.price.toLocaleString()}</p>
                          <p className="property-specs text-muted">
                            {property.bedrooms} bed • {property.bathrooms} bath • {property.sqft} sqft
                          </p>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => onRemoveFromTour(property.mlsNumber)}
                          className="remove-btn"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </div>

            {/* Schedule Form */}
            <div className="schedule-section">
              <h5 className="section-title">
                <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                {tourProperties.length === 1 
                  ? "What time would you like to tour this home?" 
                  : "What time would you like to tour these homes?"}
              </h5>
              
              <Form onSubmit={handleScheduleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Preferred Date <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={formData.preferredDate}
                        onChange={(e) => setFormData({...formData, preferredDate: e.target.value})}
                        min={today}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Preferred Time <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        value={formData.preferredTimeSlot}
                        onChange={(e) => setFormData({...formData, preferredTimeSlot: e.target.value})}
                        required
                      >
                        <option value="">Select a time</option>
                        {timeSlots.map(slot => (
                          <option key={slot} value={slot}>{slot}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mt-3 mb-3">Your Information</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.customerName}
                        onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                        placeholder="John Smith"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="email"
                        value={formData.customerEmail}
                        onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
                        placeholder="john@email.com"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="tel"
                        value={formData.customerPhone}
                        onChange={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          setFormData({...formData, customerPhone: formatted});
                        }}
                        placeholder="(555) 123-4567"
                        maxLength={14}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Financing Section */}
                <div className="financing-section mb-4">
                  <h6 className="section-subtitle">
                    <FontAwesomeIcon icon={faDollarSign} />
                    How are you planning to purchase?
                  </h6>
                  <div className="financing-options">
                    <div 
                      className={`financing-option ${formData.financingType === 'pre-qualified' ? 'selected' : ''}`}
                      onClick={() => {
                        setFormData({...formData, financingType: 'pre-qualified'});
                        setShowPreQualQuestion(true);
                      }}
                    >
                      <div className="option-icon">
                        <FontAwesomeIcon icon={faHandHoldingUsd} />
                      </div>
                      <div className="option-label">Pre-Qualified</div>
                      <div className="option-description">I have a pre-approval letter</div>
                    </div>
                    <div 
                      className={`financing-option ${formData.financingType === 'cash' ? 'selected' : ''}`}
                      onClick={() => {
                        setFormData({...formData, financingType: 'cash'});
                        setShowPreQualQuestion(false);
                      }}
                    >
                      <div className="option-icon">
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                      </div>
                      <div className="option-label">Cash</div>
                      <div className="option-description">Purchasing with cash</div>
                    </div>
                  </div>
                  
                  {showPreQualQuestion && formData.financingType === 'pre-qualified' && (
                    <Form.Group className="mt-3">
                      <Form.Label className="small">Pre-approval amount (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g., $500,000"
                        onChange={(e) => setFormData({...formData, notes: formData.notes + `\nPre-approval amount: ${e.target.value}`})}
                      />
                    </Form.Group>
                  )}
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Additional Available Dates (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.additionalDates}
                    onChange={(e) => setFormData({...formData, additionalDates: e.target.value})}
                    placeholder="e.g., Also available Saturday morning or Sunday afternoon"
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Special Requests or Notes (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Any specific requirements or questions?"
                  />
                </Form.Group>

                <Button 
                  type="submit" 
                  variant="primary" 
                  size="lg" 
                  className="w-100 schedule-tour-submit-btn"
                >
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                  Schedule Tour
                </Button>

                <p className="text-muted text-center mt-3 mb-0">
                  <small>Connecting you to a live agent shortly.</small>
                </p>
              </Form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ToursPanel;